import React, { ReactNode } from 'react';

import { NoteView } from '@ndvr/customer-api';

import { MinimalTiptapEditorNoToolbar } from '@/components/minimal-tiptap';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { convertPlainTextToHtml, formatDateTime } from 'utils';
import { MEETING_MINUTES_SECTION_LABEL_CLASSES } from 'views/calendar/meeting-minutes-list-utils';

interface NoteBodyProps {
    note: NoteView;
    personName: string;
    updatedPersonName: string;
    editorContentClassName?: string;
}

/**
 * Chrome-less read-only note body: the note content followed by the Created / Last
 * Modified footer, restyled for the new note page; NewNoteBodyCard wraps it in a Card.
 */
const NoteBody = ({ note, personName, updatedPersonName, editorContentClassName }: NoteBodyProps) => (
    <>
        <MinimalTiptapEditorNoToolbar
            key={`${note.subject}-${note.updatedAt}`}
            value={convertPlainTextToHtml(note.content)}
            className="w-full border-0 shadow-none"
            output="html"
            editable={false}
            editorClassName="focus:outline-hidden"
            editorContentClassName={editorContentClassName}
        />

        {/* Labels and text follow the Meeting Minutes sections, so the footer reads like the summary above. */}
        <div className="mt-4 grid grid-cols-1 gap-6 border-t pt-4 md:grid-cols-2">
            <div>
                <h4 className={MEETING_MINUTES_SECTION_LABEL_CLASSES}>Created</h4>
                <div className="text-cocoa-1000 text-sm">{formatDateTime(note.createdAt)}</div>
                <div className="text-cocoa-500 text-xs">by {personName}</div>
            </div>
            <div>
                <h4 className={MEETING_MINUTES_SECTION_LABEL_CLASSES}>Last modified</h4>
                <div className="text-cocoa-1000 text-sm">{formatDateTime(note.updatedAt ?? note.createdAt)}</div>
                <div className="text-cocoa-500 text-xs">by {updatedPersonName}</div>
            </div>
        </div>
    </>
);

/**
 * Read-only note body: the note content followed by the Created / Last Modified footer. A summary,
 * when given, reads first, above a divider.
 */
export const NewNoteBodyCard = ({
    summary,
    ...props
}: Omit<NoteBodyProps, 'editorContentClassName'> & { summary?: ReactNode }) => (
    <Card className="bg-white">
        <CardContent className="flex flex-col gap-6 p-6">
            {summary && (
                <>
                    {summary}
                    <Separator />
                </>
            )}
            {/* Same text treatment as the summary sections, so the card reads as one document. */}
            <NoteBody {...props} editorContentClassName="text-cocoa-1000 text-sm" />
        </CardContent>
    </Card>
);
